/**
 * Report AI strutturato (Claude Haiku 4.5) — generazione MANUALE.
 *
 * Pattern UX (refactor 2026-05-08, idea_3ff4b90ffef1):
 *  - In pagina resta solo un **recap testuale breve** (1-3 righe estratte
 *    dal report) + i bottoni "Apri report completo" / "Scarica PDF" /
 *    "Genera/Rigenera". Compatto, non ruba spazio in dashboard.
 *  - I dettagli ricchi (3 punti di forza + 3 criticità con suggerimenti +
 *    textarea piano d'azione + "Salva come piano d'azione") vivono dentro
 *    un **Dialog modale** che si apre on demand.
 *
 * Estrazione del recap: zero costo, no chiamate Claude extra. Si basa su
 * `summary` (se l'AI lo ha già fornito), altrimenti compone una sintesi
 * dal primo positivo + prima criticità del payload strutturato.
 *
 * Refactor 2026-05-29: estratti `AIInsightsModal` (Dialog content) e
 * `AIInsightsNegativeCard` (criticità + textarea + pin) in file separati.
 */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Button } from './ui/button';
import {
  Sparkles, RefreshCw, AlertTriangle, Clock, Download, ExternalLink,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { AIInsightsModal } from './AIInsightsModal';


// Polling parameters per la generazione AI in background (vedi `generate`).
const POLL_MAX_WAIT_MS = 90_000;
const POLL_INTERVAL_MS = 3_000;
const POLL_FAIL_AFTER_ATTEMPTS = 5;


function useStableRef(value) {
  const ref = React.useRef(value);
  useEffect(() => { ref.current = value; }, [value]);
  return ref;
}


export function AIInsightsPanel({
  cacheFetcher, generateFetcher, forceFetcher, pdfFetcher,
  currentSignature, scope, entityId, storeId, title = 'Report AI',
  compact = false, autoOpen = true,
}) {
  const { user } = useAuth();
  const isOps = user?.role === 'operations_manager';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [mountChecked, setMountChecked] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [open, setOpen] = useState(false);

  // Ref stabili così i callbacks polling/generate non si ricreano ad ogni render
  // del parent (i fetcher arrivano da useCallback in OverviewPage / AreaPage).
  const generateFetcherRef = useStableRef(generateFetcher);
  const forceFetcherRef = useStableRef(forceFetcher);
  const pdfFetcherRef = useStableRef(pdfFetcher);
  const cacheFetcherRef = useStableRef(cacheFetcher);

  // Carica subito la cache al mount + ogni volta che cambia il fetcher (cambio store/area).
  useEffect(() => {
    let active = true;
    setMountChecked(false);
    setData(null);
    setErr(null);
    (async () => {
      try {
        const res = await cacheFetcher();
        if (active && res?.data && res.data.structured) setData(res.data);
      } catch (_e) { /* silent: nessuna cache pronta è normale */ }
      finally { if (active) setMountChecked(true); }
    })();
    return () => { active = false; };
  }, [cacheFetcher]);

  // Pattern background+polling (2026-05-21):
  //  - generate() ora torna immediato con `{status: "generating"}` se il
  //    backend ha avviato la chiamata Claude in background.
  //  - Facciamo polling su cache_only ogni 3s fino a 90s totali. Quando
  //    `structured` compare = generazione completata, mostriamo il report
  //    e (se autoOpen) apriamo il dialog.
  const generate = useCallback(async (force = false) => {
    setLoading(true);
    setErr(null);
    try {
      const res = await (force ? forceFetcherRef.current() : generateFetcherRef.current());
      const body = res?.data || {};
      if (body.structured) {
        setData(body);
        if (autoOpen) setOpen(true);
        return;
      }
      if (body.status === 'generating') {
        const startedAt = Date.now();
        let attempt = 0;
        while (Date.now() - startedAt < POLL_MAX_WAIT_MS) {
          attempt += 1;
          await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
          try {
            const pollRes = await cacheFetcherRef.current();
            const poll = pollRes?.data || {};
            if (poll.structured) {
              setData(poll);
              if (autoOpen) setOpen(true);
              return;
            }
            if (attempt > POLL_FAIL_AFTER_ATTEMPTS && poll.cached === false && !poll.status) {
              setErr('Generazione interrotta. Riprova.');
              return;
            }
          } catch (_e) { /* polling: silent retry */ }
        }
        setErr('Generazione troppo lenta. Il report verrà mostrato al prossimo accesso.');
        return;
      }
      setErr(body.detail || 'Risposta inattesa dal server');
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [autoOpen, cacheFetcherRef, forceFetcherRef, generateFetcherRef]);

  const downloadPdf = useCallback(async () => {
    if (!pdfFetcherRef.current) return;
    setPdfLoading(true); setPdfError('');
    try {
      const res = await pdfFetcherRef.current();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const disp = res.headers?.['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/i.exec(disp);
      const filename = match ? match[1] : `report-${scope}-${entityId}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setPdfError(e.response?.data?.detail || e.message || 'Errore durante il download');
    } finally { setPdfLoading(false); }
  }, [scope, entityId, pdfFetcherRef]);

  const hasReport = !!(data && data.structured);
  const isStale = hasReport && currentSignature && data.signature && currentSignature !== data.signature;
  const generatedAtLabel = hasReport
    ? new Date(data.generated_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
    : null;

  // Recap a 1-3 righe — estratto SENZA chiamate Claude extra.
  // Strategia in cascata:
  //   1) `data.summary` se esiste (alcuni run AI già lo producono come top-level).
  //   2) Sintesi composta: primo positive + prima negative.
  //   3) Fallback: "Report disponibile — apri per i dettagli."
  const recap = useMemo(() => {
    if (!hasReport) return null;
    if (data.summary && typeof data.summary === 'string' && data.summary.trim().length > 0) {
      return data.summary.trim();
    }
    const positives = data.structured?.positives || [];
    const negatives = data.structured?.negatives || [];
    const parts = [];
    if (positives.length > 0) parts.push(`👍 ${positives[0].title}`);
    if (negatives.length > 0) parts.push(`⚠ ${negatives[0].title}`);
    if (parts.length === 0) return 'Report disponibile — apri per i dettagli.';
    return parts.join(' · ');
  }, [hasReport, data]);

  const headerProps = {
    title, hasReport, recap, loading, mountChecked, isStale,
    generatedAtLabel, model: data?.model, pdfLoading, pdfFetcher,
    onOpen: () => setOpen(true), onGenerate: () => generate(hasReport), onDownloadPdf: downloadPdf,
  };

  return (
    <div className={`bg-white border border-slate-200 ${compact ? '' : 'mt-6'}`} data-testid="ai-insights-panel">
      {compact ? <CompactHeader {...headerProps} /> : <FullHeader {...headerProps} />}

      {pdfError && (
        <div className="px-5 py-2 bg-red-50 border-b border-red-200" data-testid="ai-pdf-error">
          <p className="text-xs text-red-700">Errore PDF: {pdfError}</p>
        </div>
      )}

      {loading && !compact && (
        <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-start gap-2" data-testid="ai-loading-warning">
          <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900">
            <span className="font-semibold">Generazione in corso.</span> Non modificare i dati finché l'analisi non termina.
          </p>
        </div>
      )}

      {err && (
        <div className="px-5 py-3" data-testid="ai-error">
          <p className="text-sm text-red-600">Errore: {err}</p>
        </div>
      )}

      {!hasReport && !loading && !err && mountChecked && !compact && (
        <div className="px-5 py-4 text-sm text-slate-500" data-testid="ai-empty-state">
          <p>Nessun report ancora generato per questa vista.</p>
          <p className="text-xs text-slate-400 mt-1">L'analisi identifica 3 punti di forza e 3 criticità con suggerimenti operativi.</p>
        </div>
      )}

      <AIInsightsModal
        open={open}
        onOpenChange={setOpen}
        title={title}
        data={data}
        isOps={isOps}
        scope={scope}
        entityId={entityId}
        storeId={storeId}
        isStale={isStale}
        generatedAtLabel={generatedAtLabel}
      />
    </div>
  );
}


/* =========================================================================
 *  Header variants — entrambi mostrano: titolo + recap (se report) +
 *  timestamp + bottoni Apri/Scarica PDF/Genera. La sola differenza è il
 *  layout (verticale compatto vs orizzontale full-width).
 * ========================================================================= */

function CompactHeader({
  title, hasReport, recap, loading, mountChecked, isStale, generatedAtLabel,
  model, pdfLoading, pdfFetcher, onOpen, onGenerate, onDownloadPdf,
}) {
  return (
    <div className="px-4 md:px-5 py-4 flex flex-col gap-3 min-h-full">
      <div className="min-w-0 flex-1">
        <h3 className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-violet-600" /> {title}
        </h3>
        {!hasReport && !loading && (
          <p className="text-sm font-heading font-semibold text-slate-800 mt-1.5 leading-tight">
            Genera il report AI per ricevere 3 punti di forza e 3 criticità.
          </p>
        )}
        {hasReport && (
          <p
            className="text-sm font-heading font-semibold mt-1.5 leading-snug text-slate-800 line-clamp-2"
            data-testid="ai-recap"
            title="Anteprima del report — apri per i dettagli completi"
          >
            {recap}
          </p>
        )}
        {hasReport && (
          <p className="text-[11px] text-slate-400 mt-2 truncate" data-testid="ai-timestamp" title={`${generatedAtLabel} · ${model}`}>
            <Clock className="w-3 h-3 inline-block mr-1 -mt-0.5" />
            {generatedAtLabel}
            {isStale && (
              <span className="ml-2 text-orange-700 font-medium">· Dati cambiati</span>
            )}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mt-auto">
        {hasReport && (
          <Button
            onClick={onOpen}
            variant="default"
            size="sm"
            className="rounded-none h-8 text-xs bg-black text-white hover:bg-slate-900 flex-1 min-w-[110px]"
            data-testid="ai-open-report-btn"
          >
            <ExternalLink className="w-3 h-3 mr-1.5" />Apri report
          </Button>
        )}
        {pdfFetcher && (
          <Button
            onClick={onDownloadPdf}
            disabled={pdfLoading || loading}
            variant="outline"
            size="sm"
            className="rounded-none h-8 px-2.5"
            data-testid="ai-download-pdf-btn"
            title={hasReport ? "Scarica report PDF" : "Scarica PDF (genera AI se serve, 10-20s)"}
            aria-label="Scarica PDF"
          >
            <Download className={`w-3.5 h-3.5 ${pdfLoading ? 'animate-pulse' : ''}`} />
          </Button>
        )}
        <Button
          onClick={onGenerate}
          disabled={loading || !mountChecked}
          variant={hasReport ? 'outline' : 'default'}
          size="sm"
          className={`rounded-none h-8 text-xs ${hasReport ? 'px-2.5' : 'bg-black text-white hover:bg-slate-900 flex-1 min-w-[120px]'}`}
          data-testid={hasReport ? 'ai-regenerate-btn' : 'ai-generate-btn'}
          title={hasReport ? (isStale ? 'Dati cambiati: rigenera' : 'Rigenera report') : 'Genera report AI'}
          aria-label={hasReport ? 'Rigenera' : 'Genera report AI'}
        >
          {hasReport
            ? <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''} ${isStale ? 'text-orange-700' : ''}`} />
            : <>
                <Sparkles className={`w-3 h-3 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Analisi...' : 'Genera'}
              </>}
        </Button>
      </div>
    </div>
  );
}


function FullHeader({
  title, hasReport, recap, loading, mountChecked, isStale, generatedAtLabel,
  model, pdfLoading, pdfFetcher, onOpen, onGenerate, onDownloadPdf,
}) {
  return (
    <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0 flex-1">
        <h3 className="font-heading text-base font-semibold flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-600" /> {title}
        </h3>
        {!hasReport && !loading && (
          <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
            Clicca <span className="font-semibold">Genera report AI</span>: 3 punti di forza + 3 criticità con suggerimenti operativi.
          </p>
        )}
        {hasReport && (
          <p
            className="text-xs text-slate-700 mt-1 leading-snug"
            data-testid="ai-recap"
            title="Anteprima del report — apri per i dettagli completi"
          >
            {recap}
          </p>
        )}
        {hasReport && (
          <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1.5" data-testid="ai-timestamp">
            <Clock className="w-3 h-3" /> {generatedAtLabel} · {model}
            {isStale && (
              <span className="ml-2 text-orange-700 font-medium">· Dati cambiati: Rigenera</span>
            )}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        {hasReport && (
          <Button
            onClick={onOpen}
            variant="default"
            size="sm"
            className="rounded-none h-9 bg-black text-white hover:bg-slate-900"
            data-testid="ai-open-report-btn"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-2" /> Apri report completo
          </Button>
        )}
        {pdfFetcher && (
          <Button
            onClick={onDownloadPdf}
            disabled={pdfLoading || loading}
            variant="outline"
            size="sm"
            className="rounded-none h-9"
            data-testid="ai-download-pdf-btn"
            title={hasReport
              ? "Scarica report completo in PDF (dati + analisi + piani d'azione)"
              : "Scarica PDF: l'analisi AI viene generata in linea se non già disponibile (10–20s)"}
          >
            <Download className={`w-3.5 h-3.5 mr-2 ${pdfLoading ? 'animate-pulse' : ''}`} />
            {pdfLoading
              ? (hasReport ? 'Compongo PDF…' : 'Genero AI + PDF…')
              : 'Scarica PDF'}
          </Button>
        )}
        <Button
          onClick={onGenerate}
          disabled={loading || !mountChecked}
          variant={hasReport ? 'outline' : 'default'}
          size="sm"
          className={`rounded-none h-9 ${hasReport ? '' : 'bg-black text-white hover:bg-slate-900'}`}
          data-testid={hasReport ? 'ai-regenerate-btn' : 'ai-generate-btn'}
        >
          {hasReport
            ? <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} />
            : <Sparkles className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} />}
          {loading ? 'Analisi in corso...' : hasReport ? 'Rigenera' : 'Genera report AI'}
        </Button>
      </div>
    </div>
  );
}
